import { inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as signalR from '@microsoft/signalr';
import { NotificationService } from '@services/notification.service';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { interval, Subscription, takeWhile, tap } from 'rxjs';
import { NotificationDto } from 'src/app/dtos/notification.dto';
import { SecondsToMilliseconds } from 'src/app/shared/constants/constants';
import { Cookies } from 'src/app/shared/constants/cookie.constants';
import { isDevelopment, isLocal } from 'src/app/utils/util-helper';
import { environment } from 'src/environments/environment';
import { SignalRService } from './signalr.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationSignalRService {
  private apiUrl = environment.apiUrl + '/notification-hub';

  private hubConnection!: signalR.HubConnection;

  private cookieService = inject(CookieService);
  private notificationService = inject(NotificationService);
  private signalRService = inject(SignalRService);
  private toastr = inject(ToastrService);

  // jwtHelper = inject(JwtHelperService);

  private jwtHelper = new JwtHelperService();
  private tokenCheckSub: Subscription | null = null;

  canLog: boolean = isDevelopment() || isLocal();

  constructor() {}

  /** Starts the SignalR connection and hooks up events */
  public startConnection(): void {
    let csrf = this.cookieService.get(Cookies.CSRF_TOKEN);
    console.log('Starting a connection to signalR');
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.apiUrl}`, {
        // Always read the latest token from cookie
        accessTokenFactory: () => {
          const token = this.cookieService.get(Cookies.AUTH_TOKEN);
          if (!token) console.log('auth token is null in SignalR');
          // console.log('[SignalR] Access Token:', token);
          return token ?? '';
        }
        // Configure automatic reconnect: immediately, then after 2s, 10s, 30s
      })
      .configureLogging(signalR.LogLevel.Error) // Enable logging
      .withAutomaticReconnect([0, SecondsToMilliseconds.TWO_SECONDS, SecondsToMilliseconds.TEN_SECONDS])
      .build();

    // 1) Hook lifecycle events
    this.hubConnection.onreconnecting((error) => {
      console.warn('🔄 SignalR reconnecting:', error);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log(`🔄 SignalR Reconnected with ID: ${connectionId}`);
    });

    this.hubConnection.onclose((error) => {
      if (this.canLog) {
        console.error('❌ SignalR closed');
        console.log(`Hub state = ${this.hubConnection.state}`);
      }

      // No more heartbeats if the connection is dead
      this.stopTokenCheck();
    });

    // 2) Handle incoming notifications
    this.hubConnection.on('ReceiveNotification', (notification: NotificationDto) => {
      // console.log(`New notification received: ID = ${notification.notificationId}, CreatedAt = ${notification.createdAt}`);

      // console.log('New notification:', notification);

      const currentNotifications = this.notificationService.notificationSubject.getValue();
      // Shallow clone for change detection
      const updatedNotifications = [{ ...notification }, ...currentNotifications];

      this.notificationService.updateNotifications(updatedNotifications);

      this.toastr.success('You have a new notification!', '', {
        timeOut: SecondsToMilliseconds.FOUR_SECONDS,
        positionClass: 'toast-top-right',
        progressBar: true
      });
    });

    // 3) Start the connection
    this.hubConnection
      .start()
      .then(() => {
        if (this.canLog) {
          console.log('✅ SignalR Connected, connectionId=', this.hubConnection.connectionId);
        }

        this.startTokenCheck(); // Begin monitoring token expiry
      })
      .catch((err) => {
        console.log('Cannot connect to signalR');
        // console.error('🚨 SignalR Connection Error:', err);  // Log the error in the catch block
      });
  }

  /** Stops the SignalR connection and any token‐monitoring */
  public stopConnection(): void {
    if (this.hubConnection?.state !== signalR.HubConnectionState.Disconnected) {
      console.log('Disconnecting SignalR connectionId=', this.hubConnection.connectionId);
      this.hubConnection.stop().catch((err) => console.error('Error stopping hub', err));
    }
    this.stopTokenCheck();
  }

  /**
   * Every 10s, check if the JWT has expired.
   * If so, stop the hub and cancel further checks.
   */
  private startTokenCheck(): void {
    this.stopTokenCheck(); // clean up existing

    this.tokenCheckSub = interval(SecondsToMilliseconds.TEN_SECONDS)
      .pipe(
        // Only run while the hub is “alive”
        takeWhile(() => this.hubConnection.state !== signalR.HubConnectionState.Disconnected),
        tap(() => {
          const token = this.cookieService.get(Cookies.AUTH_TOKEN);
          if (!token || this.jwtHelper.isTokenExpired(token)) {
            console.warn('🔒 Token expired — stopping SignalR');
            this.hubConnection.stop().catch((err) => console.error('Error stopping hub', err));
          }
        })
      )
      .subscribe();
  }

  /** Unsubscribe the token-check heartbeat */
  private stopTokenCheck(): void {
    if (this.tokenCheckSub) {
      this.tokenCheckSub.unsubscribe();
      this.tokenCheckSub = null;
    }
  }

  /** Cleanup if the service is destroyed */
  ngOnDestroy() {
    this.stopConnection();
  }
}
